import React, { createContext, useContext, useState, useEffect } from 'react';

// Import translation files
import it from '../translations/it.json';
import en from '../translations/en.json';
import fr from '../translations/fr.json';
import es from '../translations/es.json';
import de from '../translations/de.json';
import { logWarn } from '../lib/log';

// Available languages with their display names and flags
export const LANGUAGES = {
  it: { name: 'Italiano', flag: '🇮🇹', code: 'it' },
  en: { name: 'English', flag: '🇬🇧', code: 'en' },
  fr: { name: 'Français', flag: '🇫🇷', code: 'fr' },
  es: { name: 'Español', flag: '🇪🇸', code: 'es' },
  de: { name: 'Deutsch', flag: '🇩🇪', code: 'de' }
};

// Translation data
const translations = {
  it,
  en, 
  fr,
  es,
  de
};

// Create context
const LanguageContext = createContext();

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('it'); // Default to Italian
  
  // Load saved language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('beautyou_language');
    if (savedLanguage && LANGUAGES[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    } else {
      // Auto-detect browser language
      const browserLang = navigator.language.substring(0, 2);
      if (LANGUAGES[browserLang]) {
        setCurrentLanguage(browserLang);
      }
    }
  }, []);
  
  // Change language and save to localStorage
  const changeLanguage = (languageCode) => {
    if (LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
      localStorage.setItem('beautyou_language', languageCode);
    }
  };
  
  // Get translation function
  const t = (key, params = {}) => {
    try {
      const keys = key.split('.');
      let value = translations[currentLanguage];
      
      // Navigate through nested keys
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          // Fallback to Italian if key not found
          value = translations.it;
          for (const fallbackKey of keys) {
            if (value && typeof value === 'object') {
              value = value[fallbackKey];
            } else {
              return key; // Return key if not found
            }
          }
          break;
        }
      }
      
      // Replace parameters in translation
      if (typeof value === 'string' && Object.keys(params).length > 0) {
        return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
          return params[param] || match;
        });
      }
      
      return value || key;
    } catch (error) {
      logWarn('Translation error for key:', key, error);
      return key;
    }
  };
  
  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages: LANGUAGES,
    currentLangInfo: LANGUAGES[currentLanguage]
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;